import React, { CSSProperties, ReactElement, useEffect, useMemo, useState } from 'react';
import { isSome, none, Option, some } from 'fp-ts/lib/Option';
import { Layout as ReactGridLayout } from 'react-grid-layout';
import { isMobileOnly, isTablet as isTabletDD, isDesktop } from 'react-device-detect';

import { ErrorData } from 'domain/types';
import Widget from 'domain/entity/widget';
import Layout, { LayoutType, LayoutElement, ConstraintsPerLayoutElement } from 'domain/entity/layout';
import { GridLayoutElement, GridLayouts } from 'adapter/in/dashboards/components/generic/ReactGrid';
import Grid, { Breakpoint, GridLayoutsAndWidgetMap } from 'adapter/in/dashboards/components/Grid';
import { WidgetBeingResized, useGridView } from 'adapter/in/dashboards/containers/Grid/useGridView';
import { useReadLayouts } from 'adapter/in/dashboards/hooks/Layouts/useReadLayouts';
import { dispatchWidgetsView, useWidgetsView } from 'adapter/in/dashboards/context/Widgets/view';
import { dispatchDashboardEdit, LAYOUT_STATUS, useDashboardEdit } from 'adapter/in/dashboards/context/EditDashboard';
import { useLayouts } from 'adapter/in/dashboards/context/Layouts';
import StyledGridSize from 'adapter/in/dashboards/containers/Grid/style';

type WidgetsLayoutError = Option<ErrorData>;

export type UseGridContainerReturn = {
    isLoading: boolean;
    gridLayouts: GridLayouts;
    allGridLayouts: GridLayouts;
    widgetMap: Map<string, Widget>;
    selectedLayoutType: LayoutType;
    error: WidgetsLayoutError;
    setAllGridLayouts: React.Dispatch<React.SetStateAction<GridLayouts>>;
    wrongSizeWidgets: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>>;
};

export const useGridContainer = (
    dashboardId: string,
    editingLayout: boolean,
    isEditEnabled: boolean,
    isMobile: boolean,
    isTablet: boolean
): UseGridContainerReturn => {
    const { resetWidgets } = dispatchWidgetsView();
    const [allGridLayouts, setAllGridLayouts] = useState<GridLayouts>({});
    const { widgets } = useWidgetsView();
    const { updateError: updateLayoutsError, updateIsLoading: updateLayoutsIsLoading } = useLayouts();
    const { selectedLayoutType, layoutStatus } = useDashboardEdit();
    const { setLayoutStatus } = dispatchDashboardEdit();

    const { isLoading: readLayoutsIsLoading, data: readLayoutsResult, error: readLayoutsError, reset: readLayoutsReset } = useReadLayouts(
        dashboardId
    );

    const filterLayouts = (layout: Layout) => {
        if (isEditEnabled) {
            return layout.type === selectedLayoutType;
        }
        if (isTablet) {
            return layout.type === LayoutType.TABLET;
        }
        if (isMobile) {
            return layout.type === LayoutType.MOBILE;
        }

        return true;
    };

    const getGridAndAuxWidgets = () => {
        if (widgets && isSome(readLayoutsResult)) {
            const auxWidgetMap = new Map(widgets.map((widget: Widget) => [widget.id, widget]));
            return [
                Object.keys(allGridLayouts).length > 0 && editingLayout && isEditEnabled
                    ? Object.fromEntries(Object.entries(allGridLayouts).filter(([key]) => key.includes(selectedLayoutType)))
                    : asGridLayouts(readLayoutsResult.value.filter(filterLayouts), editingLayout),
                auxWidgetMap,
            ];
        }
        return null;
    };

    const getAllGridLayouts = () => {
        if (widgets && isSome(readLayoutsResult)) {
            return Object.keys(allGridLayouts).length > 0 ? allGridLayouts : asGridLayouts(readLayoutsResult.value, editingLayout);
        }
        return {};
    };
    const gridAndAux = useMemo(() => getGridAndAuxWidgets(), [
        widgets,
        readLayoutsResult,
        editingLayout,
        isEditEnabled,
        allGridLayouts,
        selectedLayoutType,
    ]);
    const allGridLayoutsMemo = useMemo(() => getAllGridLayouts(), [widgets, readLayoutsResult, editingLayout, allGridLayouts]);

    const wrongSizeElements = useMemo(() => {
        const wrongSizeGridElementsWorker: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>> = {
            [LayoutType.DESKTOP]: {},
            [LayoutType.TABLET]: {},
            [LayoutType.MOBILE]: {},
        };
        if (Object.keys(allGridLayoutsMemo).length > 0) {
            Object.keys(allGridLayoutsMemo).forEach((layoutType) => {
                allGridLayoutsMemo[layoutType].forEach((gridLayoutElement) => {
                    if (!elementFitsContraints(gridLayoutElement)) {
                        wrongSizeGridElementsWorker[layoutType as LayoutType][gridLayoutElement.i] = {
                            type: layoutType as LayoutType,
                            minHeight: gridLayoutElement.minHeight,
                            minWidth: gridLayoutElement.minWidth,
                            maxHeight: gridLayoutElement.maxHeight,
                            maxWidth: gridLayoutElement.minWidth,
                        };
                    }
                });
            });
        }
        return wrongSizeGridElementsWorker;
    }, [allGridLayoutsMemo]);

    const error = useMemo(() => {
        if (isSome(readLayoutsError)) {
            return readLayoutsError;
        }
        return none;
    }, [readLayoutsError]);

    useEffect(() => {
        if (isSome(updateLayoutsError) || layoutStatus === LAYOUT_STATUS.FORCE_RESET) {
            setAllGridLayouts({});
            if (layoutStatus === LAYOUT_STATUS.FORCE_RESET) {
                setLayoutStatus(LAYOUT_STATUS.READY);
            }
        }
    }, [updateLayoutsError, layoutStatus]);

    useEffect(
        () => () => {
            readLayoutsReset();
            resetWidgets();
        },
        []
    );

    return {
        isLoading: readLayoutsIsLoading || updateLayoutsIsLoading,
        gridLayouts: gridAndAux ? (gridAndAux[0] as GridLayouts) : {},
        widgetMap: gridAndAux ? (gridAndAux![1] as Map<string, Widget>) : new Map<string, Widget>(),
        selectedLayoutType,
        error,
        allGridLayouts: allGridLayoutsMemo,
        setAllGridLayouts,
        wrongSizeWidgets: wrongSizeElements,
    };
};

export const useGridViewContainer = (
    dashboardId: string,
    editingLayout: boolean,
    isEditEnabled: boolean
): {
    data: Option<GridLayoutsAndWidgetMap>;
    isLoading: boolean;
    error: WidgetsLayoutError;
    cellSize: {
        width: number;
        height: number;
    };
    backgroundStyle: CSSProperties;
    backgroundColor: string;
    currentBreakpoint: Breakpoint;
    breakpoints: { [P: string]: number };
    containerWidth: number;
    onWidthChange: (newWidth: number) => void;
    onDragStart: () => void;
    onDragStop: () => void;
    onResizeStop: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onResize: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onBreakpointChange: (newBreakpoint: Breakpoint) => void;
    onLayoutChange: (currentLayout: GridLayoutElement[], allLayouts: GridLayouts) => void;
    editLayoutWidthStyle: string;
    wrongSizeWidgets: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>>;
    widgetBeingResized?: WidgetBeingResized;
} => {
    const {
        isLoading,
        gridLayouts,
        allGridLayouts,
        widgetMap,
        selectedLayoutType,
        error,
        setAllGridLayouts,
        wrongSizeWidgets,
    } = useGridContainer(dashboardId, editingLayout, isEditEnabled, isMobileOnly, isTabletDD);
    const {
        cellSize,
        backgroundStyle,
        backgroundColor,
        currentBreakpoint,
        breakpoints,
        containerWidth,
        editLayoutWidthStyle,
        onDragStart,
        onDragStop,
        onResize,
        onResizeStop,
        onWidthChange,
        onBreakpointChange,
        onLayoutChange,
        widgetBeingResized,
    } = useGridView(
        gridLayouts,
        allGridLayouts,
        editingLayout,
        selectedLayoutType,
        isEditEnabled,
        isDesktop,
        isTabletDD,
        setAllGridLayouts,
        wrongSizeWidgets
    );

    const data = useMemo(() => {
        if (widgetMap && widgetMap.size > 0 && gridLayouts && Object.keys(gridLayouts).length > 0) {
            return some({ gridLayouts, widgetMap });
        }
        return none;
    }, [widgetMap, gridLayouts]);

    return {
        data,
        isLoading,
        error,
        cellSize,
        backgroundStyle,
        backgroundColor,
        currentBreakpoint,
        breakpoints,
        containerWidth,
        onDragStart,
        onResize,
        onResizeStop,
        onDragStop,
        onWidthChange,
        onBreakpointChange,
        onLayoutChange,
        editLayoutWidthStyle,
        wrongSizeWidgets,
        widgetBeingResized,
    };
};

export type GridContainerProps = { dashboardId: string; isEditEnabled: boolean; editingLayout: boolean };

export const GridContainer = (props: GridContainerProps): ReactElement => {
    const { dashboardId, editingLayout, isEditEnabled } = props;
    const {
        data,
        isLoading,
        error,
        cellSize,
        backgroundStyle,
        backgroundColor,
        currentBreakpoint,
        breakpoints,
        containerWidth,
        onDragStart,
        onDragStop,
        onResize,
        onResizeStop,
        onWidthChange,
        onBreakpointChange,
        onLayoutChange,
        editLayoutWidthStyle,
        wrongSizeWidgets,
        widgetBeingResized,
    } = useGridViewContainer(dashboardId, editingLayout, isEditEnabled);

    return (
        <StyledGridSize editLayoutWidthStyle={editLayoutWidthStyle} backgroundColor={backgroundColor}>
            <Grid
                dashboardId={dashboardId}
                data={data}
                isLoading={isLoading}
                isEditEnabled={isEditEnabled}
                error={error}
                cellSize={cellSize}
                backgroundStyle={backgroundStyle}
                currentBreakpoint={currentBreakpoint}
                breakpoints={breakpoints}
                containerWidth={containerWidth}
                onResize={onResize}
                onDragStart={onDragStart}
                onResizeStop={onResizeStop}
                onDragStop={onDragStop}
                onWidthChange={onWidthChange}
                onBreakpointChange={onBreakpointChange}
                onLayoutChange={onLayoutChange}
                wrongSizeWidgets={wrongSizeWidgets}
                widgetBeingResized={widgetBeingResized}
            />
        </StyledGridSize>
    );
};

export default GridContainer;

// Utils

const asGridLayouts = (layouts: Layout[], editable: boolean) => {
    const result: GridLayouts = {};
    layouts.forEach(
        (layout: Layout) =>
            (result[layout.type] = layout.widgets.map(
                (layoutElement: LayoutElement) =>
                    ({
                        i: layoutElement.id,
                        x: layoutElement.position.x,
                        y: layoutElement.position.y,
                        w: layoutElement.size.width,
                        h: layoutElement.size.height,
                        static: !editable,
                        maxHeight: layoutElement.constraints.maxHeight,
                        maxWidth: layoutElement.constraints.maxWidth,
                        minHeight: layoutElement.constraints.minHeight,
                        minWidth: layoutElement.constraints.minWidth,
                        type: layoutElement.type,
                    } as GridLayoutElement)
            ))
    );

    return result;
};

export const elementFitsContraints = (layoutElement: GridLayoutElement): boolean =>
    layoutElement.h <= layoutElement.maxHeight &&
    layoutElement.w <= layoutElement.maxWidth &&
    layoutElement.h >= layoutElement.minHeight &&
    layoutElement.w >= layoutElement.minWidth;
